import React, { useEffect, useMemo, useState } from 'react';
import Button from '../ui/Button';
import Card from '../ui/Card';
import { Download, RefreshCw, Search, ChevronLeft, ChevronRight, Info, CheckCircle2, Clock, XCircle, ChevronDown, ExternalLink } from 'lucide-react';
import { safeApolloHref } from '../../utils/safeApolloHref';

const PagedJobTable = ({ onDownload, settings, downloadingJobId, variant = 'classic' }) => {
  const isFocused = variant === 'focused';

  // Icon + capitalised label per status, matching the reference table.
  const statusPresentation = (status) => ({
    completed: { Icon: CheckCircle2, label: 'Completed', bg: 'var(--color-success-bg)', fg: 'var(--color-success-fg)', icon: 'var(--color-success)' },
    failed:    { Icon: XCircle,      label: 'Failed',    bg: 'var(--color-danger-bg)',  fg: 'var(--color-danger-fg)',  icon: 'var(--color-danger)' },
    queued:    { Icon: Clock,        label: 'Queued',    bg: 'var(--color-warning-bg)', fg: 'var(--color-warning-fg)', icon: 'var(--color-warning)' },
    running:   { Icon: Clock,        label: 'Running',   bg: 'var(--color-info-bg)',    fg: 'var(--color-info-fg)',    icon: 'var(--color-info)' },
    pending:   { Icon: Clock,        label: 'Pending',   bg: 'var(--color-warning-bg)', fg: 'var(--color-warning-fg)', icon: 'var(--color-warning)' },
  }[status] || { Icon: Clock, label: status || 'Unknown', bg: 'var(--color-bg-inset)', fg: 'var(--color-text-secondary)', icon: 'var(--color-text-muted)' });
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [hoveredJobId, setHoveredJobId] = useState(null);

  const fetchPage = async (p = page, sBy = sortBy, sDir = sortDir, size = pageSize) => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('loginAsToken') || localStorage.getItem('token');
      const url = new URL(`${API_BASE_URL}/api/jobs/paged`);
      url.searchParams.set('page', String(p));
      url.searchParams.set('page_size', String(size));
      url.searchParams.set('sort_by', sBy);
      url.searchParams.set('sort_dir', sDir);
      const resp = await fetch(url.toString(), { headers: { 'Authorization': `Bearer ${token}` } });
      if (!resp.ok) throw new Error('Failed to fetch jobs');
      const data = await resp.json();
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPage(data.page || 1);
      setPageSize(data.page_size || 10);
      setSortBy(data.sort_by || sBy);
      setSortDir(data.sort_dir || sDir);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPage(1, 'created_at', 'desc', 10); }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(i => (i.list_name || '').toLowerCase().includes(q));
  }, [items, search]);

  const pages = Math.ceil(total / pageSize) || 1;

  const headerCell = (label, key) => (
    <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: 'var(--color-text-muted)', cursor: 'pointer' }}
        onClick={() => {
          const nextDir = sortBy === key && sortDir === 'desc' ? 'asc' : 'desc';
          setSortBy(key); setSortDir(nextDir); fetchPage(page, key, nextDir);
        }}>
      {label} {sortBy === key ? (sortDir === 'desc' ? '▼' : '▲') : ''}
    </th>
  );

  return (
    <Card>
      <Card.Header>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isFocused ? 'flex-start' : 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <Card.Title>Recent Requests</Card.Title>
            {isFocused && (
              <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: '3px 0 0' }}>
                Track your scraping requests and export your data.
              </p>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ position: 'relative' }}>
              <input
                placeholder={isFocused ? "Search by file name..." : "Search by list name"}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ padding: '8px 28px 8px 10px', border: '1px solid var(--color-border)', borderRadius: 8, fontSize: 14 }}
              />
              <Search size={14} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-disabled)' }} />
            </div>
            <Button variant="secondary" onClick={() => fetchPage(page, sortBy, sortDir)} icon={<RefreshCw size={14} />}>Refresh</Button>
          </div>
        </div>
      </Card.Header>
      <Card.Content>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
            <thead>
              <tr>
                {headerCell('Date', 'created_at')}
                {headerCell(isFocused ? 'File Name' : 'List Name', 'list_name')}
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>Search Link</th>
                {headerCell('Status', 'status')}
                {headerCell('Requested', 'number_of_contacts')}
                {headerCell('Extracted', 'result_count')}
                {isFocused && (
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>Credits</th>
                )}
                <th style={{ textAlign: 'left', padding: '10px 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>Export Data</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={isFocused ? 8 : 7} style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-muted)' }}>Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={isFocused ? 8 : 7} style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-muted)' }}>No requests found</td></tr>
              ) : (
                filtered.map(job => (
                  <tr key={job.id} style={{ borderBottom: '1px solid var(--color-bg-inset)', position: 'relative' }}>
                    <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--color-text-secondary)' }}>{new Date((job.created_at || '').includes('Z') ? job.created_at : job.created_at + 'Z').toLocaleString('en-GB', {
                      timeZone: (settings?.timezone || 'Asia/Kolkata'),
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                      hour12: true
                    })}</td>
                    <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--color-text-primary)', fontWeight: 600 }}>{job.list_name}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {safeApolloHref(job.apollo_url) ? (
                        <a href={safeApolloHref(job.apollo_url)} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-accent)', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {isFocused ? 'View Search' : 'Open'}{isFocused && <ExternalLink size={13} />}
                      </a>
                      ) : (
                        <span style={{ fontSize: 13, color: 'var(--color-text-disabled)' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', position: 'relative', zIndex: hoveredJobId === job.id ? 1001 : 'auto' }}>
                        {isFocused ? (() => {
                          const p = statusPresentation(job.status);
                          return (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                              <p.Icon size={15} color={p.icon} />
                              <span style={{
                                padding: '3px 10px',
                                borderRadius: 999,
                                background: p.bg,
                                color: p.fg,
                                fontWeight: 600,
                                fontSize: 12
                              }}>
                                {p.label}
                              </span>
                            </span>
                          );
                        })() : (
                          <span
                            style={{
                              padding: '2px 8px',
                              borderRadius: 12,
                              background: job.status === 'completed' ? 'var(--color-success-bg)' : job.status === 'failed' ? 'var(--color-danger-bg)' : 'var(--color-warning-bg)',
                              color: job.status === 'completed' ? 'var(--color-success-fg)' : job.status === 'failed' ? 'var(--color-danger-hover)' : 'var(--color-warning-hover)',
                              border: '1px solid var(--color-border)'
                            }}
                          >
                            {job.status}
                          </span>
                        )}
                        {job.status === 'failed' && job.error_message && (
                          <div
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'help',
                              color: 'var(--color-danger-hover)',
                              transition: 'transform 0.2s',
                              position: 'relative'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'scale(1.1)';
                              setHoveredJobId(job.id);
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'scale(1)';
                              setHoveredJobId(null);
                            }}
                          >
                            <Info size={16} strokeWidth={2} />

                            {/* Custom Tooltip */}
                            {hoveredJobId === job.id && (
                              <div
                                style={{
                                  position: 'absolute',
                                  top: '100%',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  marginTop: '8px',
                                  backgroundColor: 'var(--color-text-primary)',
                                  color: '#ffffff',
                                  padding: '10px 14px',
                                  borderRadius: '8px',
                                  fontSize: '13px',
                                  lineHeight: '1.5',
                                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)',
                                  zIndex: 9999,
                                  minWidth: '200px',
                                  maxWidth: '350px',
                                  whiteSpace: 'normal',
                                  wordWrap: 'break-word',
                                  pointerEvents: 'none'
                                }}
                              >
                                <div style={{
                                  fontWeight: '600',
                                  marginBottom: '6px',
                                  color: '#fca5a5',
                                  fontSize: '12px',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px'
                                }}>
                                  Failure Reason
                                </div>
                                {job.error_message}
                                {/* Tooltip arrow */}
                                <div
                                  style={{
                                    position: 'absolute',
                                    bottom: '100%',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: 0,
                                    height: 0,
                                    borderLeft: '6px solid transparent',
                                    borderRight: '6px solid transparent',
                                    borderBottom: '6px solid var(--color-text-primary)'
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{
                      padding: '10px 12px', fontSize: 13,
                      fontWeight: isFocused ? 700 : undefined,
                      color: isFocused ? 'var(--color-text-primary)' : undefined
                    }}>{(job.number_of_contacts || 0).toLocaleString()}</td>
                    <td style={{
                      padding: '10px 12px', fontSize: 13,
                      color: isFocused ? 'var(--color-success-fg)' : undefined,
                      fontWeight: isFocused ? 700 : undefined
                    }}>{job.result_count ? job.result_count.toLocaleString() : (isFocused ? '-' : '0')}</td>
                    {isFocused && (
                      <td style={{
                        padding: '10px 12px', fontSize: 13,
                        fontWeight: 700,
                        color: 'var(--color-accent)'
                      }}>{job.result_count ? job.result_count.toLocaleString() : '-'}</td>
                    )}
                    <td style={{ padding: '10px 12px' }}>
                      {job.status === 'completed' ? (
                        <Button
                          size="sm"
                          variant={isFocused ? 'secondary' : 'primary'}
                          onClick={() => onDownload(job.id, job.list_name)}
                          icon={<Download size={14} />}
                          loading={downloadingJobId === job.id}
                          disabled={downloadingJobId === job.id}
                          style={isFocused ? { background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' } : undefined}
                        >
                          {downloadingJobId === job.id
                            ? 'Preparing...'
                            : (isFocused ? <>Export <ChevronDown size={14} /></> : 'Excel')}
                        </Button>
                      ) : (
                        <span style={{ fontSize: 12, color: 'var(--color-text-disabled)' }}>
                          {isFocused ? statusPresentation(job.status).label : '\u2014'}
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            Page {page} of {pages} • {total} total
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={() => { if (page > 1) { const np = page - 1; setPage(np); fetchPage(np, sortBy, sortDir); } }} disabled={page === 1} icon={<ChevronLeft size={14} />}>Prev</Button>
            <Button variant="secondary" onClick={() => { if (page < pages) { const np = page + 1; setPage(np); fetchPage(np, sortBy, sortDir); } }} disabled={page >= pages} icon={<ChevronRight size={14} />}>Next</Button>
          </div>
        </div>
      </Card.Content>
    </Card>
  );
};

export default PagedJobTable;